import { computed, Directive, input } from '@angular/core';
import { BrnButton } from '@spartan-ng/brain/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/class.utils';

export const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors outline-none disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-primary/50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white hover:bg-primary-2',
        outline: 'border border-border bg-transparent text-text hover:bg-surface-2',
        secondary: 'bg-primary-subtle text-heading-strong hover:bg-primary-subtle/70',
        ghost: 'bg-transparent text-text hover:bg-surface-2',
        destructive: 'bg-danger text-white hover:opacity-90',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-6',
        icon: 'size-9',
      },
      rounded: {
        square: 'rounded-none',
        sm: 'rounded-sm',
        default: 'rounded',
        lg: 'rounded-lg',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'default',
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

@Directive({
  selector: 'a[appButton], button[appButton]',
  standalone: true,
  hostDirectives: [{ directive: BrnButton, inputs: ['disabled'] }],
  host: {
    '[class]': '_computedClass()',
  },
})
export class ButtonDirective {
  readonly variant = input<ButtonVariants['variant']>('default');
  readonly size = input<ButtonVariants['size']>('default');
  readonly rounded = input<ButtonVariants['rounded']>('default');
  // eslint-disable-next-line @angular-eslint/no-input-rename -- lets callers write class="..." directly, merged via cn()
  readonly userClass = input<string>('', { alias: 'class' });

  protected readonly _computedClass = computed(() =>
    cn(
      buttonVariants({ variant: this.variant(), size: this.size(), rounded: this.rounded() }),
      this.userClass(),
    ),
  );
}
